<template>
    <b-modal
        v-if="assignment"
        v-model="modal"
        :id="'assignment-edit-share-modal'"
        :ref="'assignment-edit-share-modal'"
        :title="$t('portfolio.publish_assignment')"
        size="md"
        :ok-title="assignment.ab_assignment ? $t('common.edit') : $t('portfolio.share')"
        ok-variant="primary"
        :cancel-title="$t('common.cancel')"
        cancel-variant="outline-primary"
        @ok="share"
        :no-enforce-focus="true"
        modal-class="apprentice-default-modal assignment-edit-share-modal"
        scrollable>
        <div
            class="errors"
            v-if="hasErrors">
            <b-alert
                v-if="hasErrors && description === ''"
                show
                variant="danger">
                {{ $t('portfolio.description_mandatory') }}
            </b-alert>

            <b-alert
                v-if="hasErrors && file === null && !assignment.ab_assignment"
                show
                variant="danger">
                {{ $t('portfolio.you_have_to_select_an_image') }}
            </b-alert>

            <b-alert
                v-if="hasErrors && !selectedCourses.length"
                show
                variant="danger">
                {{ $t('portfolio.you_have_to_select_one_or_more_courses') }}
            </b-alert>

            <b-alert
                v-if="hasErrors && !selectedCategories.length"
                show
                variant="danger">
                {{ $t('portfolio.you_have_to_select_one_or_more_categories') }}
            </b-alert>

            <b-alert
                v-if="hasErrors && !selectedEducationLevels.length"
                show
                variant="danger">
                {{ $t('portfolio.you_have_to_select_one_or_more_levels') }}
            </b-alert>
        </div>

        <template v-slot:modal-footer="{ ok, cancel }">
            <b-button
                class="btn-hide"
                v-if="assignment.ab_assignment && assignment.ab_assignment.active"
                variant="danger"
                @click="removeShare()">
                {{ $t('portfolio.withdraw_publication') }}
            </b-button>
            <b-button
                class="btn-cancel"
                variant="outline-primary"
                @click="cancel()"
                >{{ $t('common.cancel') }}
            </b-button>
            <b-button
                class="btn-edit-share"
                variant="primary"
                @click="ok()"
                >{{
                    assignment.ab_assignment && assignment.ab_assignment.active
                        ? $t('common.edit')
                        : $t('portfolio.publish')
                }}
            </b-button>
        </template>

        <b-form-group class="assignment-share-group-title">
            <label for="assignment-share-title">{{ $t('common.title') }}<span class="required">*</span></label>
            <b-form-input
                id="assignment-share-title"
                v-model="title"></b-form-input>
        </b-form-group>

        <b-form-group class="assignment-share-group-description">
            <label
                class="assignment-share-description-label"
                v-b-toggle="`assignment-description-collapse-${assignment.id}`"
                :for="`assignment-description-${assignment.id}`">
                <span class="title">
                    {{ $t('common.description') }}
                    <span class="required">*</span>
                </span>
                <i class="fa fa-fw fa-chevron-down"></i>
            </label>
            <b-collapse
                class="assignment-description-collapse"
                visible
                :id="`assignment-description-collapse-${assignment.id}`">
                <div id="assignment-share-description">
                    <ckeditor
                        v-model="description"
                        :id="`assignment-description-${assignment.id}`"
                        :editor="editor"
                        :config="editorConfig"></ckeditor>
                </div>
            </b-collapse>
        </b-form-group>

        <b-form-group class="assignment-share-group-banner">
            <label for="assignment-share-banner">Banner<span class="required">*</span></label>
            <div
                class="banner-preview"
                v-if="bannerImage !== ''">
                <img
                    :src="bannerImage"
                    alt="Banner preview" />
            </div>
            <b-form-file
                class="apprentice-file-upload"
                id="assignment-share-banner"
                v-model="file"
                :placeholder="
                    assignment.ab_assignment
                        ? $t('portfolio.select_a_new_banner_for_this_assignment')
                        : this.$t('portfolio.select_a_banner_for_this_assignment')
                "
                accept="image/*"
                @change="onFileImageChange($event)">
            </b-form-file>
        </b-form-group>

        <b-form-group class="assignment-share-video-upload-group">
            <label for="assignment-share-video-upload">Video</label>
            <div
                class="video-preview"
                v-if="Object.keys(videoSource).length !== 0">
                <design-2021-apprentice-video-player-component :options="videoSource">
                </design-2021-apprentice-video-player-component>
            </div>

            <b-form-file
                class="apprentice-file-upload"
                id="assignment-share-video-upload"
                v-model="videoFile"
                :placeholder="
                    assignment.ab_assignment
                        ? $t('portfolio.upload_a_new_video_for_this_assignment')
                        : $t('portfolio.upload_video_for_this_assignment')
                "
                accept="video/*"
                @change="onFileVideoChange($event)">
            </b-form-file>
        </b-form-group>

        <b-form-group
            class="assignment-share-group-course"
            :description="$t('portfolio.you_can_select_multiple_options_by_holding_down_ctrl')">
            <label for="assignment-share-courses">{{ $t('portfolio.courses') }}<span class="required">*</span></label>
            <b-form-select
                class="apprentice-primary-select"
                id="assignment-share-course"
                v-model="selectedCourses"
                :options="courses"
                multiple
                :select-size="5"
                text-field="name"
                value-field="id">
            </b-form-select>
        </b-form-group>

        <b-form-group
            class="assignment-share-group-category"
            :description="$t('portfolio.you_can_select_multiple_options_by_holding_down_ctrl')">
            <label for="assignment-share-category">{{ $t('portfolio.category') }}<span class="required">*</span></label>
            <b-form-select
                class="apprentice-primary-select"
                id="assignment-share-category"
                v-model="selectedCategories"
                multiple
                :options="categories"
                :select-size="5"
                :disabled="!selectedCourses.length"
                text-field="name"
                value-field="id">
            </b-form-select>
        </b-form-group>

        <b-form-group
            class="assignment-share-group-education-level"
            :description="$t('portfolio.you_can_select_multiple_options_by_holding_down_ctrl')">
            <label for="assignment-share-education-level"
                >{{ $t('portfolio.level') }}<span class="required">*</span></label
            >
            <b-form-select
                class="apprentice-primary-select"
                id="assignment-share-education-level"
                v-model="selectedEducationLevels"
                multiple
                :options="educationLevels"
                :select-size="4"
                value-field="id"
                text-field="name">
            </b-form-select>
        </b-form-group>

        <b-form-group class="assignment-share-group-activity">
            <b-form-checkbox
                v-model="activity"
                name="check-button"
                switch>
                {{ $t('portfolio.assignment_linked_to_activity_with_external_party') }}
            </b-form-checkbox>
        </b-form-group>

        <b-form-group
            class="assignment-share-anonymously"
            v-if="!organisationalView">
            <b-form-checkbox
                v-model="anonymous"
                name="check-button"
                switch
                unchecked-value="0"
                value="1">
                {{ $t('portfolio.publish_anonymously') }}
            </b-form-checkbox>
        </b-form-group>

        <b-form-group
            class="assignment-share-group-activity"
            v-if="canPaid">
            <b-form-checkbox
                v-model="paid"
                name="check-button"
                switch>
                {{ $t('portfolio.is_a_paid_assignment') }}
            </b-form-checkbox>
        </b-form-group>

        <b-alert
            variant="warning"
            show
            >{{ $t('common.fields_with_an_asterisk_are_mandatory') }}</b-alert
        >
    </b-modal>
</template>

<script>
import ApprenticeEditor from '../../../../helpers/ApprenticeEditor';

export default {
    name: 'design-2021-portfolio-edit-share-modal-component',
    props: ['canPaid'],
    data() {
        return {
            modal: false,
            assignment: null,
            title: null,
            description: '',
            file: null,
            selectedCategories: [],
            selectedEducationLevels: [],
            selectedCourses: [],
            bannerImage: '',
            videoFile: null,
            videoSource: {},

            hasErrors: false,
            activity: false,
            anonymous: false,
            paid: false,
            disableShare: false,

            courses: [],
            educationLevels: [],
            organisationalView: false,
            editor: ApprenticeEditor,
            editorConfig: {
                contentsCss: 'test',
                bodyClass: 'mybodyclass',
                toolbar: {
                    items: [
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        '|',
                        'link',
                        'bulletedList',
                        'numberedList',
                        'blockQuote',
                        'undo',
                        'redo',
                    ],
                },
                language: this.$authUser.locale,
                table: {
                    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
                },
            },
        };
    },
    computed: {
        categories() {
            if (!this.courses.length || !this.selectedCourses.length) {
                return [];
            }

            let categories = [];
            selectedLoop: for (const selected_course of this.selectedCourses) {
                for (const course of this.courses) {
                    if (course.id !== selected_course) {
                        continue;
                    }

                    courseCategoriesLoop: for (const course_category of course.ab_categories) {
                        for (const current_category of categories) {
                            if (current_category.id === course_category.id) {
                                continue courseCategoriesLoop;
                            }
                        }
                        categories.push(course_category);
                    }
                    continue selectedLoop;
                }
            }
            return categories;
        },
    },
    methods: {
        show(assignment, view, course_ids) {
            const edit = !!assignment.ab_assignment;
            const video = edit && !!assignment.ab_assignment.video;

            if (!course_ids) {
                course_ids = [];
            }

            if (view) {
                this.organisationalView = true;
            }

            this.assignment = assignment;
            this.title = edit ? assignment.ab_assignment.name : assignment.name;
            this.description = edit ? assignment.ab_assignment.description : '';
            this.file = null;
            this.videoFile = null;
            this.selectedCategories = edit ? assignment.ab_assignment.categories.map((category) => category.id) : [];
            this.selectedEducationLevels = edit
                ? assignment.ab_assignment.education_levels.map((educationLevel) => educationLevel.id)
                : [];
            this.selectedCourses = edit
                ? assignment.ab_assignment.courses.map((course) => {
                      return course.id;
                  })
                : course_ids;
            this.bannerImage = edit
                ? assignment.ab_assignment.banner_image.file.image
                    ? assignment.ab_assignment.banner_image.file.image.thumb_path
                    : assignment.ab_assignment.banner_image.file.public_path
                : '';
            this.activity = edit ? !!assignment.ab_assignment.activity : false;
            this.paid = edit ? !!assignment.ab_assignment.paid : false;
            this.anonymous = edit ? assignment.ab_assignment.anonymous : 0;

            this.videoSource =
                edit && video
                    ? {
                          autoplay: false,
                          controls: true,
                          sources: [
                              {
                                  src: assignment.ab_assignment.video.file.public_path,
                                  type: 'video/' + assignment.ab_assignment.video.file.extension,
                              },
                          ],
                          extension: assignment.ab_assignment.video.file.extension,
                          full_name: assignment.ab_assignment.video.file.full_name,
                      }
                    : {};

            this.getCoursesAndCategories();
            this.getEducationLevels();

            this.hasErrors = false;

            this.modal = true;
        },

        getCoursesAndCategories() {
            // todo maybe in the future do not load courses all the time
            this.axios.get(this.routes.coursesAndAbCategories()).then(
                (response) => {
                    this.courses = response.data;
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        getEducationLevels() {
            if (this.educationLevels.length) {
                return;
            }

            this.axios.get(this.routes.educationLevels()).then(
                (response) => {
                    this.educationLevels = response.data;
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        getcategories(checked) {
            const courseFound = this.selectedCourses.indexOf(checked[0]);

            if (courseFound === -1) {
                this.selectedCourses.push(checked[0]);

                if (this.selectedCourses[0] === null) {
                    this.selectedCourses = this.selectedCourses.filter((course) => course !== null);
                }
            } else {
                this.selectedCourses = this.selectedCourses.filter((course) => course !== checked[0]);
            }
        },

        share(bvModalEvt, removestatus = false) {
            this.hasErrors = false;

            if (
                (this.file === null && !this.assignment.ab_assignment) ||
                !this.selectedCategories.length ||
                !this.selectedEducationLevels.length ||
                this.description === '' ||
                !this.selectedCourses.length
            ) {
                this.hasErrors = true;
                bvModalEvt.preventDefault();
                this.$refs['assignment-edit-share-modal'].$refs.body.scrollTop = 0;
            } else {
                const data = new FormData();
                const options = {
                    headers: { 'Content-Type': 'multipart/form-data' },
                };

                data.append('name', this.title);
                data.append('description', this.description);
                data.append('assignment_id', this.assignment.id);
                data.append('activity', this.activity ? 1 : 0);
                data.append('anonymous', this.anonymous ? 1 : 0);
                data.append('paid', this.paid ? 1 : 0);

                for (let i = 0; i < this.selectedCategories.length; i++) {
                    data.append('category_ids[]', this.selectedCategories[i]);
                }

                for (let i = 0; i < this.selectedEducationLevels.length; i++) {
                    data.append('education_levels[]', this.selectedEducationLevels[i]);
                }

                for (const course_id of this.selectedCourses) {
                    data.append('course_ids[]', course_id);
                }

                this.alert.saving();
                if (this.assignment.ab_assignment) {
                    if (this.file !== null) {
                        data.append('banner_image', this.file);
                    }

                    if (this.videoFile) {
                        data.append('video', this.videoFile);
                    }

                    if (removestatus) {
                        data.append('active', 0);
                    } else {
                        data.append('active', 1);
                    }

                    axios.post('ab/assignment/' + this.assignment.ab_assignment.id, data, options).then(
                        (response) => {
                            this.$emit('update-assignments', response.data);
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                            bvModalEvt.preventDefault();
                        },
                    );
                } else {
                    data.append('banner_image', this.file);
                    data.append('active', 1);

                    if (this.videoFile) {
                        data.append('video', this.videoFile);
                    }

                    axios.post('ab/assignment', data, options).then(
                        (response) => {
                            this.$emit('update-assignments', response.data);
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                            bvModalEvt.preventDefault();
                        },
                    );
                }
            }
        },

        removeShare(checked) {
            this.share(this.$bvModal, true);
            this.$emit('remove-shared-assignments', this.assignment.id);
            this.$bvModal.hide('assignment-edit-share-modal');
        },

        courseClicked(id, event) {
            event.preventDefault();
            event.stopPropagation();

            let removed;

            this.selectedCourses = this.selectedCourses.filter((selected_course_id) => {
                if (selected_course_id === id) {
                    removed = true;
                    return false;
                }
                return true;
            });
            if (!removed) {
                this.selectedCourses.push(id);
            }
        },
        changeCategories(checked) {
            const categoryFound = this.selectedCategories.indexOf(checked[0]);

            if (categoryFound === -1) {
                this.selectedCategories.push(checked[0]);

                if (this.selectedCategories[0] === null) {
                    this.selectedCategories = this.selectedCategories.filter((category) => category !== null);
                }
            } else {
                this.selectedCategories = this.selectedCategories.filter((category) => category !== checked[0]);
            }
        },

        changeEducationLevel(checked) {
            const educationLevelFound = this.selectedEducationLevels.indexOf(checked[0]);

            if (educationLevelFound === -1) {
                this.selectedEducationLevels.push(checked[0]);

                if (this.selectedEducationLevels[0] === null) {
                    this.selectedEducationLevels = this.selectedEducationLevels.filter((level) => level !== null);
                }
            } else {
                this.selectedEducationLevels = this.selectedEducationLevels.filter((level) => level !== checked[0]);
            }
        },
        onFileImageChange(evt) {
            const file = evt.target.files[0];
            this.bannerImage = URL.createObjectURL(file);
        },
        onFileVideoChange(evt) {
            const file = evt.target.files[0];
            let ext = file.type.split('/').pop();

            this.videoSource = {
                autoplay: false,
                controls: true,
                sources: [
                    {
                        src: URL.createObjectURL(file),
                        type: 'video/' + ext,
                    },
                ],
            };
        },

        checkFormaat(file) {
            // If preview  version
            let ext = file.type.split('/').pop();
            const src = file.name;

            if (ext === 'ogv') {
                ext = 'ogg';
            }
            if (ext === 'mov' || ext === 'MOV') {
                ext = 'mp4';
            }

            return [
                {
                    type: 'video/' + ext,
                    src: src,
                },
            ];
        },

        videoIsSupported(video) {
            if (
                video[0].extension.toLowerCase() === 'ogv' ||
                video[0].extension.toLowerCase() === 'ogg' ||
                video[0].extension.toLowerCase() === 'mov' ||
                video[0].extension.toLowerCase() === 'mp4' ||
                video[0].extension.toLowerCase() === 'm4v' ||
                video[0].extension.toLowerCase() === 'webm'
            ) {
                return true;
            }

            return false;
        },
    },
};
</script>

<style scoped lang="scss">
::v-deep .assignment-edit-share-modal {
    .modal-body {
        overflow-x: hidden;
    }
}

.form-group {
    select,
    input {
        margin-bottom: 0;
    }
}

.assignment-share-video-upload-group,
.assignment-share-group-banner {
    .b-form-file {
        height: 51px;
    }
}

.assignment-share-group-description ::v-deep {
    label {
        padding-bottom: 0;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .ck-label {
        display: none;
    }
}

.assignment-share-description-label:not(.collapsed) i:before {
    content: '\f077';
}

.banner-preview {
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

    img {
        width: 100%;
        height: 150px;
        object-fit: cover;
        object-position: center;
    }
}

.video-preview {
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
}
</style>
